/**
 * Team roles and what each can do. The API enforces these; the web app uses
 * the same table to hide what someone can't do.
 */

export const TEAM_ROLES = ['head_coach', 'assistant_coach', 'viewer', 'player', 'parent'] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

/** Roles that can be given through an invite (there is one head coach per team). */
export const INVITABLE_ROLES = ['assistant_coach', 'viewer', 'player', 'parent'] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export const ROLE_LABELS: Record<TeamRole, string> = {
  head_coach: 'Head coach',
  assistant_coach: 'Assistant coach',
  viewer: 'Read only',
  player: 'Player',
  parent: 'Parent',
};

export const ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  head_coach: 'Runs the team. Can do everything, including managing members.',
  assistant_coach: 'Plans and edits sessions, keeps the roster, takes attendance, and can invite players and parents.',
  viewer: 'Can see sessions, members and plans, but can’t change anything.',
  player: 'Can see published sessions and games, and say when they can’t make it.',
  parent: 'Can see published sessions and games, and say when their child can’t make it.',
};

export const PERMISSIONS = [
  'team:read',
  'team:manage',
  'members:read',
  'members:manage',
  'invites:coaches',
  'invites:players',
  'sessions:read',
  /** See drafts and the full plan: coaching points, coach notes. */
  'sessions:details',
  'sessions:write',
  'attendance:write',
  /** Add, edit and remove players on the roster. */
  'roster:write',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Record<TeamRole, readonly Permission[]> = {
  head_coach: PERMISSIONS,
  assistant_coach: [
    'team:read',
    'members:read',
    'invites:players',
    'sessions:read',
    'sessions:details',
    'sessions:write',
    'attendance:write',
    'roster:write',
  ],
  viewer: ['team:read', 'members:read', 'sessions:read', 'sessions:details'],
  player: ['team:read', 'sessions:read'],
  parent: ['team:read', 'sessions:read'],
};

export function permissionsFor(role: TeamRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

export function can(role: TeamRole | null | undefined, permission: Permission): boolean {
  return !!role && ROLE_PERMISSIONS[role].includes(permission);
}

/** Which roles someone with `role` may invite. */
export function rolesInvitableBy(role: TeamRole | null | undefined): InvitableRole[] {
  const out: InvitableRole[] = [];
  if (can(role, 'invites:coaches')) out.push('assistant_coach', 'viewer');
  if (can(role, 'invites:players')) out.push('player', 'parent');
  return out;
}
